import {
  Accordion,
  Button,
  Divider,
  Fieldset,
  Group,
  ScrollAreaAutosize,
  Stack,
  Text,
  Toggle,
} from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGraphViewState,
  hasGraphClass,
  UNAVAILABLE_CLASS,
} from '@/shared/ui/graph-renderer';

import IconArrowBarBoth from '@tabler/icons-react/dist/esm/icons/IconArrowBarBoth.mjs';
import IconArrowsJoin from '@tabler/icons-react/dist/esm/icons/IconArrowsJoin.mjs';
import IconArrowsSplit from '@tabler/icons-react/dist/esm/icons/IconArrowsSplit.mjs';
import IconCircle from '@tabler/icons-react/dist/esm/icons/IconCircle.mjs';
import IconCircleCheck from '@tabler/icons-react/dist/esm/icons/IconCircleCheck.mjs';
import IconCornerRightDown from '@tabler/icons-react/dist/esm/icons/IconCornerRightDown.mjs';
import IconCornerRightDownDouble from '@tabler/icons-react/dist/esm/icons/IconCornerRightDownDouble.mjs';
import IconCornerRightUp from '@tabler/icons-react/dist/esm/icons/IconCornerRightUp.mjs';
import IconCornerRightUpDouble from '@tabler/icons-react/dist/esm/icons/IconCornerRightUpDouble.mjs';
import IconLayersDifference from '@tabler/icons-react/dist/esm/icons/IconLayersDifference.mjs';
import IconLayersSubtract from '@tabler/icons-react/dist/esm/icons/IconLayersSubtract.mjs';
import IconLayersUnion from '@tabler/icons-react/dist/esm/icons/IconLayersUnion.mjs';
import IconTrash from '@tabler/icons-react/dist/esm/icons/IconTrash.mjs';

export const ComplexityManagementMenu = () => {
  const api = useGraphViewState();
  const [dragAndDropEnabled, setDragAndDropEnabled] = useState(false);
  const { t } = useTranslation();
  const views = api?.views;
  const selectedNodes = api?.getSelectedNodes() ?? [];
  const selectedEdges = views?.getSelectedEdges() ?? [];
  const nodeIds = selectedNodes.map((node) => node.data.id);
  const edgeIds = selectedEdges.map((edge) => edge.data.id);
  const states = nodeIds.map((id) => views?.getNodeState(id));
  const hasNodeSelection = nodeIds.length > 0;
  const hasEdgeSelection = edgeIds.length > 0;
  const isMultipleNodes = nodeIds.length > 1;
  const isMultipleEdges = edgeIds.length > 1;
  const isCompoundNode =
    hasNodeSelection && states.every((state) => state?.isGroup);
  const isCollapsed =
    hasNodeSelection && states.every((state) => state?.isCollapsed);
  const hasChildren =
    hasNodeSelection && states.every((state) => state?.hasChildren);
  const canMakeGroup = states[0]?.canConvert;
  const canCollapseEdges = views?.canCollapseEdges(edgeIds);
  const isCompoundEdge =
    hasEdgeSelection &&
    selectedEdges.every((edge) => edge.data.type === 'compound');
  const hasUnavailable = api
    ?.getNodes()
    .some((node) => hasGraphClass(node.classes, UNAVAILABLE_CLASS));
  const ModeIcon = dragAndDropEnabled ? IconCircleCheck : IconCircle;
  if (!views) return null;
  return (
    <Fieldset
      legend={t('text.actions')}
      data-testid='complexity-management-menu'
    >
      <Stack>
        {hasUnavailable && (
          <Button
            data-testid='remove-unavailable-btn'
            onClick={() => {
              views.removeUnavailable();
            }}
            leftSection={<IconTrash size={16} />}
            size='compact-sm'
            variant='filled'
            color='red'
          >
            {t('graphViews.actions.removeUnavailable', {
              ns: 'srm',
            })}
          </Button>
        )}
        <Toggle
          data-testid='drag-drop-compound-mode-toggle'
          autoWidth
          size='lg'
          variant='light'
          w='100%'
          h='auto'
          px='sm'
          py='xs'
          active={dragAndDropEnabled}
          onActiveChange={(enabled) => {
            setDragAndDropEnabled(enabled);
            views.setDragAndDropEnabled(enabled);
          }}
        >
          <Group gap='xs' wrap='nowrap' w='100%'>
            <ModeIcon size={18} aria-hidden style={{ flexShrink: 0 }} />
            <Text span size='sm' fw={400} lh={1.4} ta='left'>
              {t('graphViews.actions.dragAndDropCompoundMode', {
                ns: 'srm',
              })}
            </Text>
          </Group>
        </Toggle>
        <ScrollAreaAutosize>
          <Accordion variant='separated'>
            <Accordion.Item key='nodes' value='nodes'>
              <Accordion.Control>
                {t('graphViews.text.nodes', {
                  ns: 'srm',
                })}
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  <Group>
                    <Button
                      className='grow'
                      data-testid='group-nodes-btn'
                      disabled={!isMultipleNodes}
                      onClick={() => {
                        views.group(nodeIds);
                      }}
                      leftSection={<IconLayersUnion />}
                    >
                      {t('graphViews.actions.groupSelected', {
                        ns: 'srm',
                      })}
                    </Button>
                    <Button
                      className='grow'
                      data-testid='ungroup-nodes-btn'
                      disabled={
                        !isCompoundNode ||
                        !hasChildren ||
                        !hasNodeSelection ||
                        isCollapsed
                      }
                      onClick={() => {
                        views.ungroup(nodeIds);
                      }}
                      leftSection={<IconLayersSubtract />}
                    >
                      {t('graphViews.actions.ungroupSelected', {
                        ns: 'srm',
                      })}
                    </Button>
                    <Button
                      className='grow'
                      data-testid='convert-to-compound-btn'
                      disabled={!canMakeGroup || isMultipleNodes}
                      onClick={() => {
                        views.convertToGroup(nodeIds[0]);
                      }}
                      leftSection={<IconLayersDifference />}
                    >
                      {t('graphViews.actions.convertToCompound', {
                        ns: 'srm',
                      })}
                    </Button>
                  </Group>
                  <Divider />
                  <Group>
                    <Button
                      className='grow'
                      data-testid='collapse-nodes-btn'
                      disabled={
                        !hasNodeSelection || isCollapsed || !isCompoundNode
                      }
                      onClick={() => {
                        nodeIds.forEach((id) => views.collapse(id));
                      }}
                      leftSection={<IconCornerRightUp />}
                    >
                      {t('graphViews.actions.collapseSelected', {
                        ns: 'srm',
                      })}
                    </Button>
                    <Button
                      className='grow'
                      data-testid='collapse-nodes-recursive-btn'
                      disabled={
                        !hasNodeSelection || isCollapsed || !isCompoundNode
                      }
                      onClick={() => {
                        nodeIds.forEach((id) => views.collapse(id, true));
                      }}
                      leftSection={<IconCornerRightUpDouble />}
                    >
                      {t('graphViews.actions.collapseSelectedRecursively', {
                        ns: 'srm',
                      })}
                    </Button>
                  </Group>
                  <Divider />
                  <Group>
                    <Button
                      className='grow'
                      data-testid='expand-nodes-btn'
                      disabled={
                        !hasNodeSelection || !isCompoundNode || !isCollapsed
                      }
                      onClick={() => {
                        nodeIds.forEach((id) => views.expand(id, false));
                      }}
                      leftSection={<IconCornerRightDown />}
                    >
                      {t('graphViews.actions.expandSelected', {
                        ns: 'srm',
                      })}
                    </Button>
                    <Button
                      className='grow'
                      data-testid='expand-nodes-recursive-btn'
                      disabled={!hasNodeSelection || !isCollapsed}
                      onClick={() => {
                        nodeIds.forEach((id) => views.expand(id, true));
                      }}
                      leftSection={<IconCornerRightDownDouble />}
                    >
                      {t('graphViews.actions.expandSelectedRecursively', {
                        ns: 'srm',
                      })}
                    </Button>
                  </Group>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
            <Accordion.Item key='edges' value='edges'>
              <Accordion.Control>
                {t('graphViews.text.edges', {
                  ns: 'srm',
                })}
              </Accordion.Control>
              <Accordion.Panel>
                <Stack>
                  <Group>
                    <Button
                      className='grow'
                      data-testid='collapse-edges-btn'
                      disabled={!isMultipleEdges || !canCollapseEdges}
                      onClick={() => {
                        views.collapseEdges(edgeIds);
                      }}
                      leftSection={<IconArrowsJoin />}
                    >
                      {t('graphViews.actions.collapseSelected', {
                        ns: 'srm',
                      })}
                    </Button>
                    <Button
                      className='grow'
                      data-testid='expand-edges-btn'
                      disabled={!hasEdgeSelection || !isCompoundEdge}
                      onClick={() => {
                        views.expandEdges(edgeIds);
                      }}
                      leftSection={<IconArrowsSplit />}
                    >
                      {t('graphViews.actions.expandSelected', {
                        ns: 'srm',
                      })}
                    </Button>
                    <Button
                      className='grow'
                      data-testid='expand-edges-recursive-btn'
                      disabled={!hasEdgeSelection || !isCompoundEdge}
                      onClick={() => {
                        views.expandEdges(edgeIds, true);
                      }}
                      leftSection={<IconArrowBarBoth />}
                    >
                      {t('graphViews.actions.expandSelectedRecursively', {
                        ns: 'srm',
                      })}
                    </Button>
                  </Group>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </ScrollAreaAutosize>
      </Stack>
    </Fieldset>
  );
};